import {computed, makeObservable, observable} from "mobx";
import {BoxType} from "./model";

export enum Language {
	EN = "en",
	DE = "de",
}

export type MultiLingualString = {
	[L in Language]?: string;
};

export type ResourceDefinition = {
	[key: string]: MultiLingualString;
};

export type LanguageSpecificStrings<D extends ResourceDefinition> = {
	[K in keyof D]: string;
};

export class Resources<D extends ResourceDefinition> {
	public language: Language;
	private map: Map<Language, LanguageSpecificStrings<D>>;

	constructor(private defaultLanguage: Language, definition: D) {
		makeObservable(this, {
			language: observable,
			strings: computed,
		});

		this.language = defaultLanguage;
		this.map = new Map();

		// create an object with language specific strings per language and store them to our map
		for (let key of Object.keys(definition)) {
			for (let lang of Object.keys(definition[key])) {
				const value = definition[key][lang as Language];
				if (value) {
					let languageSpecificStrings = this.map.get(lang as Language);
					if (!languageSpecificStrings) {
						languageSpecificStrings = {} as LanguageSpecificStrings<D>;
						this.map.set(lang as Language, languageSpecificStrings);
					}
					(languageSpecificStrings as any)[key] = value;
				}
			}
		}
	}

	get supportedLanguages() {
		return [...this.map.keys()];
	}

	get strings() {
		return this.getStrings(this.language);
	}

	// requires mustache
	// render(key:keyof D, context:{ [key:string]:any }) {
	//    return render(this.strings[key], context);
	// }

	getLanguageString(lang: Language, key: keyof D) {
		const langSpec = this.map.get(lang);
		if (langSpec) {
			return langSpec[key];
		}
	}

	getStrings(language: Language) {
		return this.map.get(language) || this.map.get(this.defaultLanguage) as LanguageSpecificStrings<D>;
	}
}

export const resources = new Resources(Language.DE, {
	SignUpTitle: {
		[Language.EN]: "Get Yourself a Locker",
		[Language.DE]: "Nehmen Sie sich ein Schließfach",
	},
	SignUpMessage: {
		[Language.EN]: "Please sign up with your mobile number to get yourself a locker.",
		[Language.DE]: "Bitte melden Sie sich mit Ihrer Handynummer an, um ein Schließfach zu erhalten.",
	},
	SignUpSuccessMessage: {
		[Language.EN]: "Thank you! In a few moments you will receive an SMS with the link to your key-card.",
		[Language.DE]: "Vielen Dank! In Kürze erhalten Sie ein SMS mit dem Link zu Ihrer Schlüsselkarte.",
	},
	SignUpFailedMessage: {
		[Language.EN]: "Error while signing you up. Check your phone number and try again. ",
		[Language.DE]: "Fehler beim Registrieren. Bitte prüfen Sie Ihre Handynummer und versuchen Sie erneut.",
	},
	MobileNumber: {
		[Language.EN]: "Mobile number",
		[Language.DE]: "Handynummer",
	},
	SignUp: {
		[Language.EN]: "Sign Up",
		[Language.DE]: "Registrieren",
	},
	MyLockers: {
		[Language.EN]: "My Lockers",
		[Language.DE]: "Meine Fächer",
	},
	AddLocation: {
		[Language.EN]: "Add Location",
		[Language.DE]: "Standort hinzufügen",
	},
	AlreadyHaveOccupancy: {
		[Language.EN]: "You already have an occupancy at this location.",
		[Language.DE]: "Sie haben bereits ein Schließfach an dieser Anlage.",
	},
	Help: {
		[Language.EN]: "Help",
		[Language.DE]: "Hilfe",
	},
	HelpDialogTitle: {
		[Language.EN]: "Help",
		[Language.DE]: "Hilfe",
	},
	ContactUrlName: {
		[Language.EN]: "Contact Information",
		[Language.DE]: "Kontaktinformation",
	},
	ContactPhoneName: {
		[Language.EN]: "Support Hotline",
		[Language.DE]: "Support-Hotline",
	},
	TermsName: {
		[Language.EN]: "Terms & Conditions",
		[Language.DE]: "ABGs",
	},
	ConnectionPending: {
		[Language.EN]: "Connecting ...",
		[Language.DE]: "Verbindung wird aufgebaut...",
	},
	ConnectionRetry: {
		[Language.EN]: "Retry",
		[Language.DE]: "Erneut versuchen",
	},
	ConnectionOnline: {
		[Language.EN]: "Connected",
		[Language.DE]: "Verbunden",
	},
	ConnectionOffline: {
		[Language.EN]: "Offline",
		[Language.DE]: "Offline",
	},
	YourLocker: {
		[Language.EN]: "Your Locker:",
		[Language.DE]: "Ihr Fach:",
	},
	AccessCodeMessage: {
		[Language.EN]:
			"To open your locker, please enter the following access code on the display of the locker facility:",
		[Language.DE]:
			"Um Ihr Fach zu öffnen, geben Sie bitte folgenden Zugriffscode am Display der Schließfach-Anlage ein:",
	},
	AccessCode: {
		[Language.EN]: "Access Code:",
		[Language.DE]: "Zugriffscode:",
	},
	ChangeLocker: {
		[Language.EN]: "Change Locker",
		[Language.DE]: "Anderes Fach wählen",
	},
	ChangeLockerFailedTitle: {
		[Language.EN]: "Change Locker failed",
		[Language.DE]: "Fach ändern fehlgeschlagen",
	},
	ChangeLockerFailedMessage: {
		[Language.EN]:
			"Please check your internet connection and try again. If the problem persists, please use the help button for further support.",
		[Language.DE]:
			"Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut. Wenn das Problem bestehen bleibt, verwenden Sie bitte den Hilfe-Button für weitere Unterstützung.",
	},
	Available: {
		[Language.EN]: "available",
		[Language.DE]: "frei",
	},
	OpenLocker: {
		[Language.EN]: "Open Locker",
		[Language.DE]: "Fach öffnen",
	},
	OpenAndKeep: {
		[Language.EN]: "Open and Keep",
		[Language.DE]: "Öffnen und Behalten",
	},
	OpenAndKeepFailedTitle: {
		[Language.EN]: "Opening Locker Failed",
		[Language.DE]: "Öffnen fehlgeschlagen",
	},
	OpenAndKeepFailedMessage: {
		[Language.EN]:
			"Please check your internet connection and try again later. If the problem persists, please use the help button for further support.",
		[Language.DE]:
			"Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut. Wenn das Problem bestehen bleibt, verwenden Sie bitte den Hilfe-Button für weitere Unterstützung.",
	},
	OpenAndRelease: {
		[Language.EN]: "Open and Release",
		[Language.DE]: "Öffnen und Zurückgeben",
	},
	OpenAndReleaseFailedTitle: {
		[Language.EN]: "Opening and Releasing Locker Failed",
		[Language.DE]: "Öffnen und Zurückgeben fehlgeschlagen",
	},
	OpenAndReleaseFailedMessage: {
		[Language.EN]:
			"Please check your internet connection and try again later. If the problem persists, please use the help button for further support.",
		[Language.DE]:
			"Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut. Wenn das Problem bestehen bleibt, verwenden Sie bitte den Hilfe-Button für weitere Unterstützung.",
	},
	Open: {
		[Language.EN]: "Open",
		[Language.DE]: "Öffnen",
	},
	NoLockers: {
		[Language.EN]: "You don't have a locker at the moment.",
		[Language.DE]: "Sie haben derzeit kein Schließfach.",
	},
	GetLocker: {
		[Language.EN]: "Get a Locker",
		[Language.DE]: "Schließfach nehmen",
	},
	DetermineLocation: {
		[Language.EN]: "Determine Location",
		[Language.DE]: "Standort bestimmen",
	},
	UseDeviceLocation: {
		[Language.EN]: "Use my device`s location",
		[Language.DE]: "Standort meines Geräts verwenden",
	},
	EnterLocationCode: {
		[Language.EN]: "Enter location code",
		[Language.DE]: "Standort-Code eingeben",
	},
	LocationCodeMessage: {
		[Language.EN]: "The location code is to be read on the display of the facility.",
		[Language.DE]: "Der Standort-Code ist rechts oben an der Anzeige der Anlage abzulesen.",
	},
	InvalidLocationCodeMessage: {
		[Language.EN]: "The entered location code is invalid. Please try again.",
		[Language.DE]: "Der eingegebene Standort-Code ist ungültig. Bitte versuchen Sie es nochmal.",
	},
	AddCubeByLocationFailedMessage: {
		[Language.EN]:
			"The desired locker facility could not be determined. Please check the accuracy of your device`s location and make sure you are close to the desired locker facility.",
		[Language.DE]:
			"Die gewünschte Schließfachanlage konnte nicht bestimmt werden. Bitte prüfen Sie die Genauigkeit des Standorts Ihres Geräts und stellen Sie sicher, dass Sie sich nahe an der gewünschten Schließfachanlage befinden.",
	},
	OccupyErrorMessage: {
		[Language.EN]: "An error occured during assignment of a locker. Please try again later.",
		[Language.DE]: "Beim Zuweisen eines Faches ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.",
	},
	InsufficientStorageMessage: {
		[Language.EN]: "The locker facility is currently fully occupied. Please try again later.",
		[Language.DE]: "Es ist momentan leider kein Fach frei. Bitte versuchen Sie er später erneut.",
	},
	OccupyPendingMessage: {
		[Language.EN]: "Occupying locker...",
		[Language.DE]: "Fach wird belegt...",
	},
	Released: {
		[Language.EN]: "Released",
		[Language.DE]: "Zurückgegeben",
	},
	GracePeriodOnly: {
		[Language.EN]: "Only",
		[Language.DE]: "Nur noch",
	},
	GracePeriodMinutesLeftToOpen: {
		[Language.EN]: "minutes left to open",
		[Language.DE]: "Minuten zum Öffnen",
	},
	CloseMessage: {
		[Language.EN]: "Please store your belongings and close the door.",
		[Language.DE]: "Bitte Waren verstauen und Türe schließen.",
	},
	UpdateTitle: {
		[Language.EN]: "Installing Update",
		[Language.DE]: "Update wird installiert",
	},
	UpdatePendingMessage: {
		[Language.EN]: "The application is being updated. Please wait until the application reloads.",
		[Language.DE]: "Die Anwendung wird aktualisiert. Bitte warten Sie, bis sich die Anwendung neu lädt.",
	},
	Cancel: {
		[Language.EN]: "Cancel",
		[Language.DE]: "Abbrechen",
	},
	BoxTypeAccessible: {
		[Language.EN]: "Accessible",
		[Language.DE]: "Barrierefrei",
	},
	BoxTypeStandard: {
		[Language.EN]: "Standard",
		[Language.DE]: "Standard",
	},
	BoxTypeSmall: {
		[Language.EN]: "Small",
		[Language.DE]: "Klein",
	},
	BoxTypeMedium: {
		[Language.EN]: "Medium",
		[Language.DE]: "Mittel",
	},
	BoxTypeLarge: {
		[Language.EN]: "Large",
		[Language.DE]: "Groß",
	},
	BoxTypeExtraLarge: {
		[Language.EN]: "Extra Large",
		[Language.DE]: "Sehr Groß",
	},
	BoxTypeExtraExtraLarge: {
		[Language.EN]: "Extra Extra Large",
		[Language.DE]: "Über-Groß",
	},
	AndroidPwaPrompt: {
		[Language.EN]: "Add to Home Screen",
		[Language.DE]: "Zum Startbildschirm hinzufügen",
	},
	Install: {
		[Language.EN]: "Install",
		[Language.DE]: "Installieren",
	},
	IosPwaPrompt: {
		[Language.EN]:
			"Tap <i name=\"iosShareIcon\"></i> and then <strong>\"Add to Home Screen\"</strong> to install this app.",
		[Language.DE]:
			"Tippen Sie auf <i name=\"iosShareIcon\"></i> und dann auf <strong>\"Zum Startbildschirm hinzufügen\"</strong>, um diese App zu installieren.",
	},
});

// try to push preferred user language
const languages = navigator.languages || [navigator.language || (navigator as any).userLanguage];
for (let language of languages) {
	const langCode = language.substr(0, 2);
	const index = resources.supportedLanguages.indexOf(langCode as Language);
	if (index >= 0) {
		resources.language = resources.supportedLanguages[index];
		break;
	}
}

export function getBoxTypeName(boxType: BoxType | string) {
	switch (boxType) {
		case BoxType.Accessible:
			return resources.strings.BoxTypeAccessible;

		case BoxType.Standard:
			return resources.strings.BoxTypeStandard;

		case BoxType.Small:
		case BoxType.S:
			return resources.strings.BoxTypeSmall;

		case BoxType.Medium:
		case BoxType.M:
			return resources.strings.BoxTypeMedium;

		case BoxType.Large:
		case BoxType.L:
			return resources.strings.BoxTypeLarge;

		case BoxType.XL:
			return resources.strings.BoxTypeExtraLarge;

		case BoxType.XXL:
			return resources.strings.BoxTypeExtraExtraLarge;
	}
}
